import { useCallback, useState } from 'react'
import { AUTH_ENV, OBSERVABILITY_ENV } from '@/shared/config/env'
import { Icon } from '@/shared/ui/Icon'
import { LegalLinks } from '@/shared/ui/LegalLinks'
import { useAuth } from './AuthProvider'

const DEFAULT_EMAIL_HINT = 'admin@clinica-vital.local'

/**
 * Controlled Beta login (21st.dev-inspired). A "smokey" animated backdrop plus
 * a glassmorphism auth card over the existing email/password flow.
 *
 * Deliberate, security-preserving adaptations of the template:
 *   - The smokey backdrop is pure CSS (drifting blurred gradients), NOT WebGL —
 *     no canvas/shader dependency, and it honours ``prefers-reduced-motion``.
 *   - Auth still goes through the existing ``useAuth().login`` → cookie/BFF
 *     bootstrap; no tokens ever touch JS. CSRF/session behaviour is unchanged.
 *   - Google is a DISABLED placeholder gated by ``VITE_GOOGLE_OAUTH_ENABLED``
 *     (Block E); public signup is OFF by default — invite-only during beta.
 */
export function LoginScreen() {
  const { login, error, clearError } = useAuth()
  const [email, setEmail] = useState(DEFAULT_EMAIL_HINT)
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (submitting) return
      const cleanEmail = email.trim().toLowerCase()
      if (!cleanEmail || !password) return
      setSubmitting(true)
      try {
        await login({ email: cleanEmail, password })
      } catch {
        // error already set on auth state
      } finally {
        setSubmitting(false)
      }
    },
    [email, password, login, submitting],
  )

  return (
    <div className="relative grid min-h-screen w-full place-items-center overflow-hidden bg-bg-page px-6 py-12">
      {/* Smokey backdrop — drifting, blurred gradient blobs. Decorative only:
          aria-hidden + pointer-events-none, and animation stops under
          prefers-reduced-motion (motion-reduce:animate-none). */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -left-[12%] top-[6%] h-[520px] w-[620px] max-w-full rounded-full animate-smoke-a motion-reduce:animate-none"
          style={{
            background:
              'radial-gradient(closest-side, rgba(124,92,255,0.30), rgba(124,92,255,0.05) 60%, transparent 72%)',
            filter: 'blur(72px)',
          }}
        />
        <div
          className="absolute -right-[14%] bottom-[2%] h-[560px] w-[680px] max-w-full rounded-full animate-smoke-b motion-reduce:animate-none"
          style={{
            background:
              'radial-gradient(closest-side, rgba(72,120,255,0.24), rgba(72,120,255,0.04) 58%, transparent 72%)',
            filter: 'blur(84px)',
          }}
        />
        <div
          className="absolute left-1/2 top-1/2 h-[440px] w-[780px] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full animate-smoke-a motion-reduce:animate-none"
          style={{
            background:
              'radial-gradient(closest-side, rgba(167,139,250,0.16), transparent 72%)',
            filter: 'blur(64px)',
            animationDelay: '-9s',
          }}
        />
      </div>

      <form
        onSubmit={onSubmit}
        className="relative z-10 mx-auto flex w-full max-w-[420px] flex-col gap-5 rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl"
        style={{
          boxShadow:
            '0 30px 80px -30px rgba(124,92,255,0.55), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex flex-col items-center gap-2 text-center">
          <div
            className="grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/[0.04] text-violet"
            aria-hidden="true"
          >
            <Icon name="sparkles" size={18} />
          </div>
          <h1 className="m-0 text-[22px] font-medium tracking-[-0.01em] text-text">
            Entrar no Rager
          </h1>
          <p className="m-0 text-[13px] leading-relaxed text-text-soft">
            Acesse seu workspace de consulta documental.
          </p>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11.5px] font-medium uppercase tracking-[0.14em] text-text-mute">
            Email
          </span>
          <input
            type="email"
            data-testid="login-email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (error) clearError()
            }}
            required
            placeholder="seu@email.com"
            className="rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-[14px] text-text outline-none transition-[border-color,background-color] duration-150 placeholder:text-text-dim focus:border-violet/50 focus:bg-white/[0.06]"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11.5px] font-medium uppercase tracking-[0.14em] text-text-mute">
            Senha
          </span>
          <input
            type="password"
            data-testid="login-password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              if (error) clearError()
            }}
            required
            placeholder="••••••••"
            className="rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-[14px] text-text outline-none transition-[border-color,background-color] duration-150 placeholder:text-text-dim focus:border-violet/50 focus:bg-white/[0.06]"
          />
        </label>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-400/30 bg-red-400/10 px-3 py-2 text-[12.5px] text-red-200"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          data-testid="login-submit"
          disabled={submitting || !email.trim() || !password}
          className="mt-1 grid h-11 w-full place-items-center rounded-xl text-[14px] font-medium text-white transition-[transform,box-shadow,opacity] duration-150 disabled:cursor-not-allowed disabled:opacity-50 enabled:hover:-translate-y-0.5"
          style={{
            background: 'linear-gradient(180deg, var(--violet) 0%, var(--violet-deep) 100%)',
            boxShadow:
              'inset 0 1px 0 rgba(255,255,255,0.2), 0 8px 22px -6px var(--violet-glow)',
          }}
        >
          {submitting ? 'Entrando…' : 'Entrar'}
        </button>

        {/* LGPD Block E — Google OAuth is a DISABLED placeholder. It only
            renders when VITE_GOOGLE_OAUTH_ENABLED=true (default false ⇒ the
            email/password screen is unchanged). The full flow lands in Block E2;
            the button is intentionally non-functional. */}
        {OBSERVABILITY_ENV.googleOAuth.enabled && (
          <div
            className="flex flex-col gap-2"
            data-testid="google-oauth-placeholder"
          >
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.14em] text-text-dim">
              <span className="h-px flex-1 bg-white/10" />
              ou
              <span className="h-px flex-1 bg-white/10" />
            </div>
            <button
              type="button"
              disabled
              aria-disabled="true"
              title="Login com Google estará disponível em breve"
              data-testid="google-oauth-button"
              className="grid h-11 w-full cursor-not-allowed place-items-center rounded-xl border border-white/10 bg-white/[0.03] text-[14px] font-medium text-text-soft opacity-60"
            >
              Entrar com Google (em breve)
            </button>
          </div>
        )}

        {/* Controlled Beta — invite-only. Public self-signup is DISABLED by
            default (no account-creation form anywhere in this block). When
            VITE_PUBLIC_SIGNUP_ENABLED=true we add a link to invite instructions;
            we still never collect a new password here. */}
        <p
          data-testid="signup-invite"
          className="m-0 text-center text-[12px] leading-relaxed text-text-soft"
        >
          Não tem uma conta? O acesso é por convite durante o beta.
          {AUTH_ENV.publicSignupEnabled && (
            <>
              {' '}
              <a
                href={AUTH_ENV.inviteUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="signup-invite-link"
                className="font-medium text-violet underline-offset-2 transition-colors duration-150 hover:underline"
              >
                Solicitar acesso
              </a>
            </>
          )}
        </p>

        <p className="m-0 text-center text-[11px] leading-relaxed text-text-dim">
          Dev local: use o usuário criado pelo bootstrap-admin
          (<span className="font-mono">{DEFAULT_EMAIL_HINT}</span>) com a
          senha definida no setup.
        </p>

        <div className="border-t border-white/10 pt-4">
          <LegalLinks />
        </div>
      </form>
    </div>
  )
}
